import { useState, useEffect } from 'react';
import { Settings, Bell, Moon, Sun, Globe, Monitor, Shield, HelpCircle, Check, X } from 'lucide-react';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';
import { useTheme } from '../../contexts/ThemeContext';

interface AppSettings {
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    rentReminders: boolean;
    maintenanceUpdates: boolean;
    leaseAlerts: boolean;
    marketingEmails: boolean;
  };
  appearance: {
    theme: 'light' | 'dark' | 'system';
    compactMode: boolean;
  };
  privacy: {
    showOnlineStatus: boolean;
    readReceipts: boolean;
  };
  language: string;
  timezone: string;
}

const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
];

const LANGUAGES = [
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'zh', label: '中文 (Chinese)' },
  { value: 'vi', label: 'Tiếng Việt (Vietnamese)' },
  { value: 'ar', label: 'العربية (Arabic)' },
];

export default function RenterSettings() {
  const [settings, setSettings] = useState<AppSettings>({
    notifications: {
      email: true,
      sms: false,
      push: true,
      rentReminders: true,
      maintenanceUpdates: true,
      leaseAlerts: true,
      marketingEmails: false,
    },
    appearance: {
      theme: 'light',
      compactMode: false,
    },
    privacy: {
      showOnlineStatus: true,
      readReceipts: true,
    },
    language: 'en-AU',
    timezone: 'Australia/Sydney',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const { setTheme } = useTheme();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get<any>('/users/me/settings');
      if (response) {
        setSettings(prev => ({ ...prev, ...response }));
      }
    } catch (err) {
      // Use defaults if no settings saved yet
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put('/users/me/settings', settings);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateNotification = (key: keyof AppSettings['notifications'], value: boolean) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value }
    }));
  };


  const updateAppearance = (key: keyof AppSettings['appearance'], value: any) => {
    setSettings(prev => ({
      ...prev,
      appearance: { ...prev.appearance, [key]: value }
    }));
    // Apply theme immediately
    if (key === 'theme') {
      setTheme(value);
    }
  };

  const updatePrivacy = (key: keyof AppSettings['privacy'], value: boolean) => {
    setSettings(prev => ({
      ...prev,
      privacy: { ...prev.privacy, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <PageHeader
        title="Settings"
        subtitle="Customize your app experience"
        icon={<Settings className="w-8 h-8" />}
        backPath="/dashboard"
      />

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{success}</span>
        </div>
      )}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <X className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-6">
        {/* Notification Preferences */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6">
            <Bell className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
          </div>

          <div className="space-y-6">
            {/* Notification Channels */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Notification Channels</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-900">Email Notifications</p>
                    <p className="text-sm text-gray-500">Receive updates via email</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.email}
                    onChange={(e) => updateNotification('email', e.target.checked)}
                    className="w-5 h-5 text-primary rounded focus:ring-secondary"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-900">SMS Notifications</p>
                    <p className="text-sm text-gray-500">Receive text messages for urgent updates</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.sms}
                    onChange={(e) => updateNotification('sms', e.target.checked)}
                    className="w-5 h-5 text-primary rounded focus:ring-secondary"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-900">Push Notifications</p>
                    <p className="text-sm text-gray-500">Receive in-app notifications</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.push}
                    onChange={(e) => updateNotification('push', e.target.checked)}
                    className="w-5 h-5 text-primary rounded focus:ring-secondary"
                  />
                </label>
              </div>
            </div>

            {/* Notification Types */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Notification Types</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-900">Rent Reminders</p>
                    <p className="text-sm text-gray-500">Get reminded before rent is due</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.rentReminders}
                    onChange={(e) => updateNotification('rentReminders', e.target.checked)}
                    className="w-5 h-5 text-primary rounded focus:ring-secondary"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-900">Maintenance Updates</p>
                    <p className="text-sm text-gray-500">Updates on your maintenance requests</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.maintenanceUpdates}
                    onChange={(e) => updateNotification('maintenanceUpdates', e.target.checked)}
                    className="w-5 h-5 text-primary rounded focus:ring-secondary"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-900">Lease Alerts</p>
                    <p className="text-sm text-gray-500">Important lease-related notifications</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.leaseAlerts}
                    onChange={(e) => updateNotification('leaseAlerts', e.target.checked)}
                    className="w-5 h-5 text-primary rounded focus:ring-secondary"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-900">Marketing Emails</p>
                    <p className="text-sm text-gray-500">Tips, news, and promotional content</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.marketingEmails}
                    onChange={(e) => updateNotification('marketingEmails', e.target.checked)}
                    className="w-5 h-5 text-primary rounded focus:ring-secondary"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6">
            <Monitor className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Appearance</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
              <div className="flex gap-3">
                <button
                  onClick={() => updateAppearance('theme', 'light')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                    settings.appearance.theme === 'light'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Sun className="w-5 h-5" />
                  <span>Light</span>
                </button>
                <button
                  onClick={() => updateAppearance('theme', 'dark')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                    settings.appearance.theme === 'dark'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Moon className="w-5 h-5" />
                  <span>Dark</span>
                </button>
                <button
                  onClick={() => updateAppearance('theme', 'system')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                    settings.appearance.theme === 'system'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Monitor className="w-5 h-5" />
                  <span>System</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Note: Dark mode is coming soon. Currently using light mode.
              </p>
            </div>

            <label className="flex items-center justify-between cursor-pointer pt-2">
              <div>
                <p className="font-medium text-gray-900">Compact Mode</p>
                <p className="text-sm text-gray-500">Reduce spacing for more content on screen</p>
              </div>
              <input
                type="checkbox"
                checked={settings.appearance.compactMode}
                onChange={(e) => updateAppearance('compactMode', e.target.checked)}
                className="w-5 h-5 text-primary rounded focus:ring-secondary"
              />
            </label>
          </div>
        </div>

        {/* Language & Region */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6">
            <Globe className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Language & Region</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <select
                value={settings.language}
                onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.value} value={lang.value}>{lang.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
              <select
                value={settings.timezone}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Privacy */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Privacy</h2>
          </div>

          <div className="space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium text-gray-900">Show Online Status</p>
                <p className="text-sm text-gray-500">Let others see when you're online</p>
              </div>
              <input
                type="checkbox"
                checked={settings.privacy.showOnlineStatus}
                onChange={(e) => updatePrivacy('showOnlineStatus', e.target.checked)}
                className="w-5 h-5 text-primary rounded focus:ring-secondary"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium text-gray-900">Read Receipts</p>
                <p className="text-sm text-gray-500">Let others know when you've read their messages</p>
              </div>
              <input
                type="checkbox"
                checked={settings.privacy.readReceipts}
                onChange={(e) => updatePrivacy('readReceipts', e.target.checked)}
                className="w-5 h-5 text-primary rounded focus:ring-secondary"
              />
            </label>
          </div>
        </div>

        {/* Help & Support */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6">
            <HelpCircle className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Help & Support</h2>
          </div>

          <div className="space-y-3">
            <a
              href="https://help.rentry.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-gray-900">Help Center</span>
              <span className="text-secondary">→</span>
            </a>

            <a
              href="mailto:support@rentry.com.au"
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-gray-900">Contact Support</span>
              <span className="text-secondary">→</span>
            </a>

            <a
              href="https://rentry.com.au/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-gray-900">Privacy Policy</span>
              <span className="text-secondary">→</span>
            </a>

            <a
              href="https://rentry.com.au/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-gray-900">Terms of Service</span>
              <span className="text-secondary">→</span>
            </a>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">Rentry v1.0.0</p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="px-6 py-3 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 transition-colors font-medium"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

